import getLocale from '@/utils/i18n/getLocale';

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      if (e.target?.result) {
        resolve(e.target.result as string);
      } else {
        reject(new Error('Failed to convert file'));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsDataURL(file);
  });
};

export function base64ImageToBlob(str: string): Blob {
  const pos = str.indexOf(';base64,');
  const type = str.substring(5, pos);
  const b64 = str.substring(pos + 8);

  const imageContent = atob(b64);

  const buffer = new ArrayBuffer(imageContent.length);
  const view = new Uint8Array(buffer);

  for (let n = 0; n < imageContent.length; n++) {
    view[n] = imageContent.charCodeAt(n);
  }

  const blob = new Blob([buffer], { type });
  return blob;
}

export const convertBase64ToFile = (base64: string) => {
  const blob = base64ImageToBlob(base64);
  const file = new File(
    [blob],
    `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${
      blob.type.split('/')[1]
    }`,
    {
      type: blob.type,
    },
  );
  return file;
};

export const getFileType = (file: File) => {
  const fileType = file.name
    .substring(file.name.lastIndexOf('.'))
    .toLowerCase();
  return fileType;
};

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)}kb`;
  return `${(bytes / (1024 * 1024)).toFixed(2)}mb`;
}

export function formatFileSizeLabel(bytes: number): string {
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) return `${Math.round(kilobytes)} KB`;

  const megabytes = (kilobytes / 1024).toLocaleString(getLocale(), {
    maximumFractionDigits: 1,
  });

  return `${megabytes} MB`;
}

export function formatFileType(fileType: string): string {
  return fileType.split('/').at(-1)?.toUpperCase() || '';
}

function getUrlPathname(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return '';
  }
}

export function getFileExtensionFromUrl(url: string): string {
  const pathname = getUrlPathname(url);
  const lastDot = pathname.lastIndexOf('.');
  const lastSlash = pathname.lastIndexOf('/');
  if (lastDot <= lastSlash) return '';
  return pathname.slice(lastDot + 1).toUpperCase();
}

export function getFileNameFromUrl(url: string): string {
  const pathname = getUrlPathname(url);
  return decodeURIComponent(pathname.slice(pathname.lastIndexOf('/') + 1));
}

export type FileValidationError = { type: 'format' | 'size'; file: File };

type ValidateFileParams = {
  file: File;
  accept: string;
  maxSize: number;
};

export function validateFile({
  file,
  accept,
  maxSize,
}: ValidateFileParams): FileValidationError | undefined {
  const acceptedTypes = accept
    .split(',')
    .map(type => type.trim().toLowerCase());

  if (!acceptedTypes.includes(getFileType(file))) {
    return { type: 'format', file };
  }
  if (file.size > maxSize) return { type: 'size', file };
  return undefined;
}
